import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Text, View } from '@constants/Themed';
import { ScrollView, Pressable, Platform, useWindowDimensions } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import apis from '@api/api';
import * as Animatable from 'react-native-animatable';
import { FormikProps } from 'formik';
import LottieView from '@utils/lottieAnimation';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { FEEDBACK_MODAL_HIDDEN, SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import { Dialog, XStack, YStack, SizableText } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { MaterialIcons } from '@expo/vector-icons';

export type FeedbackModalHandle = {
  openModal: () => void;
};


const FeedbackModal = forwardRef((props, ref) => {
  const feedbackVisible = useSelector((state: DefaultRootState) => state.modals.feedbackModal.modalDisplayed);
  const [visible, setVisible] = useState(false);
  const currentScreen = useSelector((state: DefaultRootState) => state.navigation.page);
  const [feedbackTypeSelected, setFeedbackTypeSelected] = useState(undefined);
  const formRef = useRef<FormikProps<any>>(null);
  const scrollRef = useRef<ScrollView>(null);
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const theme = themeSelector();
  // Consider device wide if width >= 768px (tablet/desktop threshold) or landscape orientation
  const isWideDevice = screenWidth >= 768 || screenWidth > screenHeight * 1.2;

  const dispatch = useDispatch();
  
  useEffect(() => {
    if (feedbackVisible) {
      setVisible(true);
    }
  }, [feedbackVisible])

  useImperativeHandle(ref, () => ({
    openModal: () => {
      setVisible(true);
    }
  }));

  const feedbackTypes = [
    {
      label: 'Sad',
      value: 'sad',
      icon: <LottieView source={require('@assets/animations/emojis/sad-emoji.json')} autoPlay style={{ flex: 1 }} loop />,
      message: 'We are sorry to hear that you are sad. Please tell us what happened.'
      // icon: <></>
    },
    {
      label: 'Meh!',
      value: 'meh',
      icon: <LottieView source={require('@assets/animations/emojis/unhappy-emoji.json')} style={{ flex: 1 }} loop autoPlay />,
      message: 'We are sorry to hear that you are unhappy. Please tell us what happened.'
    },
    {
      label: 'Neutral',
      value: 'neutral',
      icon: <LottieView source={require('@assets/animations/emojis/nuetral-emoji.json')} autoPlay loop style={{ flex: 1 }} />,
      message: 'We are sorry to hear that you are neutral. Please tell us what happened.'
    },
    {
      label: 'Happy',
      value: 'happy',
      icon: <LottieView source={require('@assets/animations/emojis/happy-emoji.json')} autoPlay loop style={{ flex: 1 }} />,
      message: 'We are happy to hear that you are happy. Please tell us what happened.'
    },
    {
      label: 'Amazed',
      value: 'amazed',
      icon: <LottieView source={require('@assets/animations/emojis/amazed-emoji.json')} autoPlay style={{ flex: 1 }} loop />,
      message: 'We are happy to hear that you are amazed. Please tell us what happened.'
    },
    {
      label: 'Other',
      value: 'other',
      icon: <LottieView source={require('@assets/animations/emojis/other-emoji.json')} autoPlay style={{ flex: 1 }} loop />,
      message: 'Unsure how you feel? No worries, let us know what happened.'
    }
  ]

  const { status, data, error, isFetching, isLoading, refetch } = useQuery({
    queryKey: ['feedbackSchema'],
    queryFn: async () => {
      const data = await apis.getFeedbackSchema()
        .then(res => {
          return res
        });
      return data
    },
    refetchOnMount: false,
    retryOnMount: false,
    staleTime: 1000 * 60 * 60 * 10,
    retry: 3,
    refetchOnWindowFocus: false
  })

  useEffect(() => {
    if (feedbackTypeSelected && scrollRef.current) {
      const selectedIndex = feedbackTypes.findIndex(item => item.value === feedbackTypeSelected);
      const scrollX = isWideDevice
        ? selectedIndex * (screenWidth / 6) + 20 // Adjust for wide device item width
        : selectedIndex * 60 + 20; // Original calculation for narrow devices
      scrollRef.current?.scrollTo({ x: scrollX, y: 0, animated: true });
    }
  }, [feedbackTypeSelected, scrollRef, isWideDevice, screenWidth])

  const handleSubmitFeedback = async (values) => {
    formRef.current?.setSubmitting(true);
    values.feedback_type = feedbackTypeSelected;
    values.page_details = currentScreen;
    await apis.postFeedback(values).then((res) => {
      if (res.status == 200) {
        setVisible(false);
        formRef.current?.resetForm();
        setFeedbackTypeSelected(undefined);
        setTimeout(() => dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Feedback submitted successfully', type: 'success' } }), 200);
      }
    })
  }

  const handleClose = () => {
    setFeedbackTypeSelected(undefined);
    dispatch({ type: FEEDBACK_MODAL_HIDDEN });
  }

  if (!feedbackVisible) return null;

  return (
    <Dialog open={visible} onOpenChange={(open) => {
      if (!open) {
        handleClose();
      }
      setVisible(open);
    }}>
      <Dialog.Portal>
        <Dialog.Overlay
          animation="quick"
          opacity={0.6}
          style={{
            zIndex: 1000,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            ...(Platform.OS === 'web' && {
              backdropFilter: 'blur(8px)',
            })
          }}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />
        <Dialog.Content
          bordered
          elevate
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          x={0}
          scale={1}
          style={[
            {
              padding: 0,
              marginHorizontal: 10,
              minWidth: 500,
              width: 700,
              maxWidth: "100%",
              height: "75%",
              maxHeight: "85%",
              borderRadius: 24,
              overflow: 'hidden',
              elevation: 12,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 12 },
              shadowOpacity: 0.15,
              shadowRadius: 32,
              backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
              borderWidth: 1,
              borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
            },
            Platform.OS === 'web' && {
              backdropFilter: 'blur(24px)',
              boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
            }
          ]}
          opacity={1}
          y={0}
        >
          {/* Header */}
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            paddingHorizontal: 24,
            paddingVertical: 20,
            borderBottomWidth: 1,
            borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
          }}>
            <XStack alignItems="center" gap="$3">
              <View style={{
                backgroundColor: theme ? "#f0f9ff" : "#0c4a6e",
                borderRadius: 12,
                padding: 10,
              }}>
                <MaterialIcons
                  name="feedback"
                  size={20}
                  color={theme ? "#0ea5e9" : "#38bdf8"}
                />
              </View>
              <YStack flex={1}>
                <Dialog.Title style={{
                  fontSize: 20,
                  fontWeight: '700',
                  color: theme ? '#1e293b' : '#f1f5f9',
                  marginBottom: 4,
                  margin: 0,
                }}>
                  Something to say?
                </Dialog.Title>
                <Dialog.Description style={{
                  fontSize: 14,
                  color: theme ? '#64748b' : '#94a3b8',
                  fontWeight: '500',
                  margin: 0,
                }}>
                  Looks like you may be having some trouble? Even if you aren't, nothing makes us happier than hearing from you!
                </Dialog.Description>
              </YStack>
            </XStack>
          </View>

          {/* Content */}
          <View style={{ flex: 1, padding: 24, height: "67%" }}>
            <YStack flex={1} gap="$4">
              {/* Emoji Selection Section */}
              <View style={{
                backgroundColor: theme ? '#f8fafc' : '#374151',
                borderRadius: 16,
                padding: 20,
                alignSelf: 'stretch',
                borderWidth: 1,
                minHeight: 300,
                flex: 1,
                borderColor: theme ? '#e5e7eb' : '#4b5563',
              }}>
                <SizableText 
                  style={{
                    color: theme ? '#374151' : '#f9fafb',
                    fontWeight: '600',
                    fontSize: 14,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    marginBottom: 16,
                  }}
                >
                  How are you feeling?
                </SizableText>
                <ScrollView 
                  ref={scrollRef} 
                  centerContent 
                  style={{ flex: 1, minHeight: 130 }} 
                  horizontal={isWideDevice || feedbackTypeSelected ? true : false}
                  showsHorizontalScrollIndicator={false}
                >
                  <XStack gap="$2" alignItems="center">
                    {feedbackTypes.map((item, index) => {
                      // Calculate item width based on device width and selection state
                      const itemWidth = isWideDevice
                        ? Math.min(screenWidth / 6, 150)
                        : feedbackTypeSelected
                        ? 80
                        : Math.min(screenWidth * 0.92 - 40, 300);
                      return (
                        <Pressable 
                          key={index} 
                          onPress={() => setFeedbackTypeSelected(item.value)} 
                          style={{
                            height: 120,
                            width: itemWidth,
                            marginHorizontal: 8,
                            borderRadius: 16,
                            backgroundColor: feedbackTypeSelected === item.value 
                              ? (theme ? '#3b82f6' : '#2563eb')
                              : (theme ? '#ffffff' : '#1f2937'),
                            padding: 12,
                            borderWidth: 2,
                            borderColor: feedbackTypeSelected === item.value 
                              ? (theme ? '#2563eb' : '#1d4ed8')
                              : (theme ? '#e5e7eb' : '#4b5563'),
                            elevation: feedbackTypeSelected === item.value ? 4 : 1,
                            shadowColor: '#000',
                            shadowOffset: { width: 0, height: 2 },
                            shadowOpacity: feedbackTypeSelected === item.value ? 0.2 : 0.1,
                            shadowRadius: 4,
                          }}
                        >
                          <View style={{ flex: 3, backgroundColor: 'transparent', justifyContent: 'center', alignItems: 'center' }}>
                            {item.icon}
                          </View>
                          <Animatable.Text 
                            animation={'slideInUp'} 
                            style={{ 
                              flex: 1, 
                              color: feedbackTypeSelected === item.value 
                                ? '#ffffff' 
                                : (theme ? '#1f2937' : '#f9fafb'), 
                              textAlign: 'center', 
                              overflow: 'visible',
                              fontWeight: '600',
                              fontSize: 12,
                              marginTop: 8,
                            }}
                          >
                            {item.label}
                          </Animatable.Text>
                        </Pressable>
                      )
                    })}
                  </XStack>
                </ScrollView>
                {feedbackTypeSelected && (
                  <Text style={{
                    textAlign: "center",
                    marginTop: 16,
                    color: theme ? '#64748b' : '#94a3b8',
                    fontSize: 14,
                    fontStyle: 'italic',
                  }}>
                    {feedbackTypes.find(item => item.value === feedbackTypeSelected)?.message}
                  </Text>
                )}
              </View>

              {/* Feedback Form Section */}
              {feedbackTypeSelected && (
                <Animatable.View 
                  useNativeDriver 
                  animation={'fadeIn'} 
                  style={{
                    backgroundColor: theme ? '#f8fafc' : '#374151',
                    borderRadius: 16,
                    padding: 20,
                    borderWidth: 1,
                    borderColor: theme ? '#e5e7eb' : '#4b5563',
                  }}
                >
                  <SizableText 
                    style={{
                      color: theme ? '#374151' : '#f9fafb',
                      fontWeight: '600',
                      fontSize: 14,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      marginBottom: 16,
                    }}
                  >
                    Leave us some feedback (please)
                  </SizableText>
                  <Text style={{
                    color: theme ? '#64748b' : '#94a3b8',
                    fontSize: 12,
                    marginBottom: 16,
                  }}>
                    We will get back to you as soon as possible
                  </Text>
                  <GenerateSchemaForm 
                    ref={formRef} 
                    hideLabel={false} 
                    schema={data?.schema} 
                    nullify={['userId', 'feedback_type', 'page_details']} 
                    removeSubmit 
                    submitFunction={handleSubmitFeedback}
                    formStyle={{
                      backgroundColor: 'transparent',
                    }}
                    fieldStyle={{
                      backgroundColor: theme ? '#ffffff' : '#1f2937',
                      borderColor: theme ? '#e5e7eb' : '#4b5563',
                      borderRadius: 12,
                      padding: 16,
                      marginBottom: 16,
                    }}
                    labelStyle={{
                      color: theme ? '#374151' : '#f9fafb',
                      fontWeight: '600',
                      fontSize: 14,
                      marginBottom: 8,
                    }}
                  />
                </Animatable.View>
              )}
            </YStack>
          </View>

          {/* Footer Actions */}
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            paddingHorizontal: 24,
            paddingVertical: 16,
            borderTopWidth: 1,
            borderTopColor: theme ? '#e5e7eb' : '#4b5563',
          }}>
            <XStack gap="$3" justifyContent="flex-end">
              <Dialog.Close asChild>
                <Pressable
                  onPress={handleClose}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: theme ? '#f1f5f9' : '#4b5563',
                    borderWidth: 1,
                    borderColor: theme ? '#e2e8f0' : '#6b7280',
                    minWidth: 100,
                    alignItems: 'center',
                  }}
                >
                  <Text style={{
                    color: theme ? '#64748b' : '#d1d5db',
                    fontWeight: '600',
                    fontSize: 14,
                  }}>
                    Cancel
                  </Text>
                </Pressable>
              </Dialog.Close>
              <Pressable
                onPress={() => formRef.current?.handleSubmit()}
                disabled={!feedbackTypeSelected}
                style={{
                  paddingHorizontal: 20,
                  paddingVertical: 12,
                  borderRadius: 12,
                  backgroundColor: feedbackTypeSelected 
                    ? (theme ? '#3b82f6' : '#2563eb')
                    : (theme ? '#cbd5e1' : '#4b5563'),
                  minWidth: 140,
                  alignItems: 'center',
                  elevation: feedbackTypeSelected ? 2 : 0,
                  shadowColor: feedbackTypeSelected ? '#3b82f6' : 'transparent',
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.2,
                  shadowRadius: 8,
                  opacity: feedbackTypeSelected ? 1 : 0.6,
                }}
              >
                <Text style={{
                  color: '#ffffff',
                  fontWeight: '600',
                  fontSize: 14,
                }}>
                  Submit Feedback
                </Text>
              </Pressable>
            </XStack>
          </View>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  )
});
export default FeedbackModal;